'use client';

import { c as _c } from "react/compiler-runtime";
import { useRenownAuth } from '@powerhousedao/reactor-browser';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useOperatorDrive } from '@/modules/shared/hooks/use-operator-drive';
import { useTeamAdminDrive } from '@/modules/shared/hooks/use-team-admin-drive';
import { AlreadyCompletedCard } from './already-completed-card';
import { AlreadyOperatorCard } from './already-operator-card';
import { ConnectAccountStep } from './connect-account-step';
import { DriveCheckErrorCard } from './drive-check-error-card';
import { FullPageSpinner } from './full-page-spinner';
import { OnboardingShell } from './onboarding-shell';
import { OnboardingSteps } from './onboarding-steps';
import { UpgradeToOperatorCard } from './upgrade-to-operator-card';

// Bridges the operator intent across Renown's login, which does a full-page
// redirect that drops the query string (see the `operatorIntent` handling below).
const INTENT_KEY = 'achra:offer-services-intent';
function GetStartedFlow() {
  const $ = _c(20);
  const auth = useRenownAuth();
  const searchParams = useSearchParams();
  const isAuthResolving = auth.status === "loading";
  const isAuthenticated = auth.status === "authorized" && Boolean(auth.address);
  const {
    drivesQuery,
    hasTeamAdminDrive
  } = useTeamAdminDrive();
  const {
    operatorDrive,
    hasOperatorDrive
  } = useOperatorDrive();
  let t0;
  if ($[0] !== searchParams) {
    t0 = () => {
      if (typeof window === "undefined") {
        return false;
      }
      return searchParams.get("intent") === "operator" || window.sessionStorage.getItem(INTENT_KEY) === "operator";
    };
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [operatorIntent] = useState(t0);
  let t1;
  let t2;
  if ($[2] !== operatorIntent || $[3] !== searchParams) {
    t1 = () => {
      if (searchParams.get("intent") === "operator") {
        window.sessionStorage.setItem(INTENT_KEY, "operator");
      } else {
        if (operatorIntent) {
          window.sessionStorage.removeItem(INTENT_KEY);
        }
      }
    };
    t2 = [searchParams, operatorIntent];
    $[2] = operatorIntent;
    $[3] = searchParams;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  const [justCompleted, setJustCompleted] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  if (isAuthResolving) {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <FullPageSpinner />;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  if (!isAuthenticated) {
    let t3;
    if ($[7] !== auth.login) {
      t3 = <OnboardingShell currentStep={1}><ConnectAccountStep onLogin={auth.login} /></OnboardingShell>;
      $[7] = auth.login;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (drivesQuery.isPending) {
    let t3;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <FullPageSpinner />;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  if (drivesQuery.isError) {
    let t3;
    if ($[10] !== drivesQuery) {
      t3 = <div className="mx-auto w-full max-w-xl"><DriveCheckErrorCard onRetry={() => void drivesQuery.refetch()} /></div>;
      $[10] = drivesQuery;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  if (hasTeamAdminDrive && !justCompleted) {
    if (operatorIntent) {
      if (hasOperatorDrive && !upgradeOpen) {
        const t3 = operatorDrive?.driveLink;
        let t4;
        if ($[12] !== t3) {
          t4 = <div className="mx-auto w-full max-w-xl"><AlreadyOperatorCard operatorDriveLink={t3} /></div>;
          $[12] = t3;
          $[13] = t4;
        } else {
          t4 = $[13];
        }
        return t4;
      }
      let t3;
      if ($[14] !== upgradeOpen) {
        t3 = <div className="mx-auto w-full max-w-xl"><UpgradeToOperatorCard open={upgradeOpen} onOpenChange={setUpgradeOpen} /></div>;
        $[14] = upgradeOpen;
        $[15] = t3;
      } else {
        t3 = $[15];
      }
      return t3;
    }
    let t3;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="mx-auto w-full max-w-xl"><AlreadyCompletedCard /></div>;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    return t3;
  }
  const t3 = operatorIntent ? "operator" : undefined;
  let t4;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setJustCompleted(true);
    };
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t3) {
    t5 = <OnboardingSteps defaultPersonaId={t3} onComplete={t4} />;
    $[18] = t3;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
export { GetStartedFlow };